import React from 'react';
import { ListItem, Typography, Box, Chip, Tooltip, IconButton } from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { Folder, ChevronRight } from '@mui/icons-material';
import { GruppoProgetti } from '../types/gruppo';

interface GruppoProgettiListItemProps {
  gruppo: GruppoProgetti;
  onClick?: () => void;
}

const GruppoProgettiListItem: React.FC<GruppoProgettiListItemProps> = ({ gruppo, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `gruppo-${gruppo.id}`,
    data: { gruppo, type: 'gruppo' },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: gruppo.colore ? `${gruppo.colore}15` : 'background.paper',
        mb: 1,
        borderRadius: 1,
        border: '2px solid',
        borderColor: gruppo.colore || 'divider',
        borderStyle: 'dashed',
        '&:hover': {
          bgcolor: gruppo.colore ? `${gruppo.colore}25` : 'action.hover',
          borderStyle: 'solid',
          borderColor: gruppo.colore || 'primary.main',
        },
        position: 'relative',
        pr: onClick ? 1 : undefined,
      }}
    >
      <Box
        {...listeners}
        {...attributes}
        sx={{
          flex: 1,
          display: 'flex',
          cursor: isDragging ? 'grabbing' : 'grab',
          minWidth: 0,
          alignItems: 'center',
        }}
      >
        <Folder sx={{ mr: 2, color: gruppo.colore || '#FFC107', flexShrink: 0, fontSize: 28 }} />
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {gruppo.nome}
            </Typography>
            <Chip
              label={`${gruppo.progetti.length} prog.`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                bgcolor: gruppo.colore || 'primary.main',
                color: '#fff',
              }}
            />
          </Box>

          {gruppo.descrizione && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {gruppo.descrizione}
            </Typography>
          )}

          {gruppo.progetti.length > 0 && (
            <Tooltip
              title={
                <Box>
                  {gruppo.progetti.map((p) => (
                    <Typography key={p.id} variant="caption" display="block">
                      {p.id_hrp ? `[${p.id_hrp}] ` : ''}
                      {p.titolo}
                    </Typography>
                  ))}
                </Box>
              }
              placement="right"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  fontStyle: 'italic',
                  cursor: 'help',
                }}
              >
                {gruppo.progetti
                  .slice(0, 2)
                  .map((p) => (p.id_hrp ? `[${p.id_hrp}]` : p.titolo))
                  .join(', ')}
                {gruppo.progetti.length > 2 && ` +${gruppo.progetti.length - 2}`}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </Box>

      {onClick && (
        <Tooltip title="Apri gruppo">
          <IconButton
            size="small"
            onClick={onClick}
            sx={{ ml: 1, color: gruppo.colore || 'primary.main', flexShrink: 0 }}
          >
            <ChevronRight />
          </IconButton>
        </Tooltip>
      )}
    </ListItem>
  );
};

export default GruppoProgettiListItem;
